import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CornerLabelField } from "@/components/ui/corner-label-field";
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { apiClient } from '@/lib/api-client';
import { useState, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { useAlert } from "@/hooks/use-alert";
import { useLocation } from "wouter";
import { Upload, X } from 'lucide-react';
import { LogoLoader } from '@/components/ui/logo-loader';
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ProfilePhotoCropModal } from "@/components/ProfilePhotoCropModal";
import { getSortedCountries, getCountryName } from '@/constants/countries';
import { PhoneInput } from '@/components/ui/phone-input';
import { getDefaultCountry, getDefaultCountryAsync } from '@/utils/country-detection';
import { type CountryCode } from 'libphonenumber-js';
import { API_ROUTES } from '@mangelapp/shared';
import { useOrganizationStore } from '@/stores/organizationStore';

type AccountTabProps = {
  currentUserCode: string | null;
  currentUser: any;
  formValues: any;
  setFormValues: (updater: any) => void;
  setHasChanges: (v: boolean) => void;
  setShowPasswordDialog: (open: boolean) => void;
  hasPermission: (perm: string) => boolean;
  preferences: any;
  preferencesLoading: boolean;
  updatePreferences: { mutate: (payload: any) => void };
  activeOrgRole: string | null;
  activeOrganizationId?: string | null;
};

export default function AccountTab({
  currentUserCode,
  currentUser,
  formValues,
  setFormValues,
  setHasChanges,
  setShowPasswordDialog,
  hasPermission,
  preferences,
  preferencesLoading,
  updatePreferences,
  activeOrgRole,
  activeOrganizationId,
}: AccountTabProps) {
  const { t, i18n } = useTranslation(['pages.settings']);
  const { toast } = useToast();
  const { confirm, prompt } = useAlert();
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const clearOrganization = useOrganizationStore((s) => s.clearOrganization);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [defaultCountry, setDefaultCountry] = useState<CountryCode>(getDefaultCountry());

  // Test WhatsApp mutation
  const testWhatsAppMutation = useMutation({
    mutationFn: async () => {
      return apiClient.testWhatsApp();
    },
    onSuccess: (data: any) => {
      toast({
        title: t('pages.settings:sections.whatsapp.testSuccess'),
        description: data?.message || t('pages.settings:sections.whatsapp.testSuccessDescription'),
      });
    },
    onError: (error: any) => {
      toast({
        title: t('pages.settings:sections.whatsapp.testFailed'),
        description: error?.message || t('pages.settings:sections.whatsapp.testFailedDescription'),
        variant: 'destructive',
      });
    },
  });

  // Detect country with IP geolocation when component loads
  useEffect(() => {
    getDefaultCountryAsync().then((detectedCountry) => {
      if (detectedCountry) {
        setDefaultCountry(detectedCountry);
      }
    }).catch(() => {
      // Silently fail - we already have a fallback from getDefaultCountry()
    });
  }, []);
  
  const profilePhotoUrl = (currentUser as any)?.profilePhotoUrl || formValues.profilePhotoUrl || '';
  
  // Fetch organization-scoped positions
  const { data: positionsResponse } = useQuery({
    queryKey: ["/api/positions", activeOrganizationId],
    queryFn: async () => {
      const url = activeOrganizationId 
        ? `/api/positions?organizationId=${encodeURIComponent(activeOrganizationId)}`
        : "/api/positions";
      const response = await apiRequest(url);
      return await response.json();
    },
  });

  // Fetch global user positions (used in registration)
  const { data: userPositionsResponse } = useQuery({
    queryKey: ["/api/user-positions"],
    queryFn: async () => {
      const response = await apiRequest("/api/user-positions");
      return await response.json();
    },
  });

  const positions = positionsResponse?.data || [];
  const userPositions = userPositionsResponse?.positions || [];

  const { data: includedProjectsResponse, isLoading: isIncludedProjectsLoading } = useQuery({
    queryKey: [API_ROUTES.PROJECTS.LIST, 'included', activeOrganizationId],
    queryFn: async () => {
      const url = activeOrganizationId
        ? `${API_ROUTES.PROJECTS.LIST}?organizationId=${encodeURIComponent(activeOrganizationId)}`
        : API_ROUTES.PROJECTS.LIST;
      const response = await apiRequest(url);
      return await response.json();
    },
  });
  const includedProjects = Array.isArray(includedProjectsResponse?.data)
    ? includedProjectsResponse.data
    : [];

  const leaveProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      const leaveEndpoint =
        (API_ROUTES.PROJECTS as any).LEAVE?.(projectId) ||
        `/api/projects/${projectId}/members/me`;
      const response = await apiRequest(leaveEndpoint, {
        method: 'DELETE',
      });
      return await response.json();
    },
    onSuccess: (_data, projectId) => {
      toast({
        title: t('pages.settings:sections.includedProjects.leaveSuccessTitle'),
        description: t('pages.settings:sections.includedProjects.leaveSuccessDescription'),
      });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST, 'included', activeOrganizationId] });
      queryClient.removeQueries({ queryKey: [API_ROUTES.PROJECTS.GET(projectId)] });
      queryClient.removeQueries({ queryKey: [API_ROUTES.PROJECTS.GET(projectId), 'light'] });

      if (location.startsWith(`/project/${projectId}`)) {
        setLocation('/projects');
      }
    },
    onError: (error: any) => {
      const message = error?.message || t('pages.settings:sections.includedProjects.leaveErrorDescription');
      toast({
        title: t('pages.settings:sections.includedProjects.leaveErrorTitle'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  const canDeleteProjectsFromAccount =
    hasPermission('manage_projects') || hasPermission('manage_all_projects');

  const deleteProjectFromAccountMutation = useMutation({
    mutationFn: async (projectId: string) => {
      return await apiClient.deleteProject(projectId);
    },
    onSuccess: (_data, projectId) => {
      toast({
        title: t('pages.settings:sections.includedProjects.deleteSuccessTitle'),
        description: t('pages.settings:sections.includedProjects.deleteSuccessDescription'),
      });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST, 'included', activeOrganizationId] });
      queryClient.invalidateQueries({ queryKey: ['/api/stats'] });
      queryClient.removeQueries({ queryKey: [API_ROUTES.PROJECTS.GET(projectId)] });
      queryClient.removeQueries({ queryKey: [API_ROUTES.PROJECTS.GET(projectId), 'light'] });

      if (location.startsWith(`/project/${projectId}`)) {
        setLocation('/projects');
      }
    },
    onError: (error: any) => {
      const message = error?.message || t('pages.settings:sections.includedProjects.deleteErrorDescription');
      toast({
        title: t('pages.settings:sections.includedProjects.deleteErrorTitle'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  const { data: includedOrgsResponse, isLoading: isIncludedOrgsLoading } = useQuery({
    queryKey: [API_ROUTES.ORGS.MY_ORGANIZATIONS],
    queryFn: async () => {
      const response = await apiRequest(API_ROUTES.ORGS.MY_ORGANIZATIONS);
      return await response.json();
    },
  });
  const includedOrganizations = Array.isArray(includedOrgsResponse?.data)
    ? includedOrgsResponse.data
    : [];

  const leaveOrganizationMutation = useMutation({
    mutationFn: async (orgId: string) => {
      const leaveEndpoint = API_ROUTES.ORGS.LEAVE(orgId);
      const response = await apiRequest(leaveEndpoint, {
        method: 'DELETE',
      });
      const data = await response.json();
      if (!response.ok) {
        const err: any = new Error(data?.message || 'Failed to leave organization');
        err.response = response;
        err.data = data;
        throw err;
      }
      return data;
    },
    onSuccess: async (_data, orgId) => {
      await queryClient.refetchQueries({ queryKey: [API_ROUTES.ORGS.MY_ORGANIZATIONS] });
      await queryClient.refetchQueries({ queryKey: ['/api/orgs/me'] });
      await queryClient.refetchQueries({ queryKey: ['auth', 'me'] });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST, 'included', activeOrganizationId] });
      if (activeOrganizationId === orgId) {
        clearOrganization();
        setLocation('/dashboard');
      }
      toast({
        title: t('pages.settings:sections.includedOrganizations.leaveSuccessTitle'),
        description: t('pages.settings:sections.includedOrganizations.leaveSuccessDescription'),
      });
    },
    onError: (error: any) => {
      const message = error?.message || error?.data?.message || t('pages.settings:sections.includedOrganizations.leaveErrorDescription');
      toast({
        title: t('pages.settings:sections.includedOrganizations.leaveErrorTitle'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  const { data: demoSettingsResponse, isLoading: isDemoSettingsLoading } = useQuery({
    queryKey: [activeOrganizationId ? API_ROUTES.ORGS.DEMO_SETTINGS(activeOrganizationId) : 'demo-settings', activeOrganizationId],
    enabled: Boolean(activeOrganizationId) && hasPermission('manage_organization'),
    queryFn: async () => {
      if (!activeOrganizationId) return { data: { hideDemoProjects: false } };
      const response = await apiRequest(API_ROUTES.ORGS.DEMO_SETTINGS(activeOrganizationId));
      return await response.json();
    },
  });

  const hideDemoProjects = demoSettingsResponse?.data?.hideDemoProjects === true;

  const updateDemoSettingsMutation = useMutation({
    mutationFn: async (nextHideDemoProjects: boolean) => {
      if (!activeOrganizationId) return null;
      const response = await apiRequest(API_ROUTES.ORGS.DEMO_SETTINGS(activeOrganizationId), {
        method: 'PUT',
        body: JSON.stringify({ hideDemoProjects: nextHideDemoProjects }),
      });
      return await response.json();
    },
    onMutate: async (nextHideDemoProjects: boolean) => {
      if (!activeOrganizationId) return {};

      const demoSettingsKey = [API_ROUTES.ORGS.DEMO_SETTINGS(activeOrganizationId), activeOrganizationId] as const;
      const projectsKey = [API_ROUTES.PROJECTS.LIST] as const;
      const includedProjectsKey = [API_ROUTES.PROJECTS.LIST, 'included', activeOrganizationId] as const;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: demoSettingsKey }),
        queryClient.cancelQueries({ queryKey: projectsKey }),
        queryClient.cancelQueries({ queryKey: includedProjectsKey }),
      ]);

      const previousDemoSettings = queryClient.getQueryData(demoSettingsKey);
      const previousProjects = queryClient.getQueryData(projectsKey);
      const previousIncludedProjects = queryClient.getQueryData(includedProjectsKey);

      queryClient.setQueryData(demoSettingsKey, (old: any) => ({
        ...old,
        data: {
          ...(old?.data || {}),
          hideDemoProjects: nextHideDemoProjects,
        },
      }));

      if (nextHideDemoProjects) {
        const filterDemoProjects = (payload: any) => {
          if (!payload || !Array.isArray(payload?.data)) return payload;
          return {
            ...payload,
            data: payload.data.filter((project: any) => project?.isDemo !== true),
          };
        };

        queryClient.setQueryData(projectsKey, filterDemoProjects);
        queryClient.setQueryData(includedProjectsKey, filterDemoProjects);
      }

      return { demoSettingsKey, projectsKey, includedProjectsKey, previousDemoSettings, previousProjects, previousIncludedProjects };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST, 'included', activeOrganizationId] });
      if (activeOrganizationId) {
        queryClient.invalidateQueries({ queryKey: [API_ROUTES.ORGS.DEMO_SETTINGS(activeOrganizationId), activeOrganizationId] });
      }
      toast({
        title: t('pages.settings:sections.demoProjects.updateSuccessTitle'),
        description: t('pages.settings:sections.demoProjects.updateSuccessDescription'),
      });
    },
    onError: (error: any, _variables, context) => {
      if (context?.demoSettingsKey && context?.previousDemoSettings !== undefined) {
        queryClient.setQueryData(context.demoSettingsKey, context.previousDemoSettings);
      }
      if (context?.projectsKey && context?.previousProjects !== undefined) {
        queryClient.setQueryData(context.projectsKey, context.previousProjects);
      }
      if (context?.includedProjectsKey && context?.previousIncludedProjects !== undefined) {
        queryClient.setQueryData(context.includedProjectsKey, context.previousIncludedProjects);
      }
      toast({
        title: t('pages.settings:sections.demoProjects.updateErrorTitle'),
        description: error?.message || t('pages.settings:sections.demoProjects.updateErrorDescription'),
        variant: 'destructive',
      });
    },
  });

  const deleteAllDemoProjectsMutation = useMutation({
    mutationFn: async () => {
      if (!activeOrganizationId) return null;
      const response = await apiRequest(API_ROUTES.ORGS.DEMO_PROJECTS(activeOrganizationId), {
        method: 'DELETE',
      });
      return await response.json();
    },
    onMutate: async () => {
      if (!activeOrganizationId) return {};

      const projectsKey = [API_ROUTES.PROJECTS.LIST] as const;
      const includedProjectsKey = [API_ROUTES.PROJECTS.LIST, 'included', activeOrganizationId] as const;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: projectsKey }),
        queryClient.cancelQueries({ queryKey: includedProjectsKey }),
      ]);

      const previousProjects = queryClient.getQueryData(projectsKey);
      const previousIncludedProjects = queryClient.getQueryData(includedProjectsKey);

      const filterDemoProjects = (payload: any) => {
        if (!payload || !Array.isArray(payload?.data)) return payload;
        return {
          ...payload,
          data: payload.data.filter((project: any) => project?.isDemo !== true),
        };
      };

      queryClient.setQueryData(projectsKey, filterDemoProjects);
      queryClient.setQueryData(includedProjectsKey, filterDemoProjects);

      return { projectsKey, includedProjectsKey, previousProjects, previousIncludedProjects };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST] });
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.PROJECTS.LIST, 'included', activeOrganizationId] });
      toast({
        title: t('pages.settings:sections.demoProjects.deleteSuccessTitle'),
        description: t('pages.settings:sections.demoProjects.deleteSuccessDescription'),
      });
    },
    onError: (error: any, _variables, context) => {
      if (context?.projectsKey && context?.previousProjects !== undefined) {
        queryClient.setQueryData(context.projectsKey, context.previousProjects);
      }
      if (context?.includedProjectsKey && context?.previousIncludedProjects !== undefined) {
        queryClient.setQueryData(context.includedProjectsKey, context.previousIncludedProjects);
      }
      toast({
        title: t('pages.settings:sections.demoProjects.deleteErrorTitle'),
        description: error?.message || t('pages.settings:sections.demoProjects.deleteErrorDescription'),
        variant: 'destructive',
      });
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async ({ password }: { password: string }) => {
      return apiClient.deleteAccount(password, 'DELETE');
    },
    onSuccess: (data: any) => {
      toast({
        title: t('pages.settings:sections.deleteAccount.successTitle'),
        description: data?.message || t('pages.settings:sections.deleteAccount.successMessage'),
      });
      apiClient.setAccessToken(null);
      queryClient.clear();
      clearOrganization();
      setLocation('/login');
    },
    onError: (error: any) => {
      const message = error?.message || t('pages.settings:sections.deleteAccount.errorMessage');
      toast({
        title: t('pages.settings:sections.deleteAccount.errorTitle'),
        description: message,
        variant: 'destructive',
      });
    },
  });

  const handleDeleteAccount = async () => {
    const confirmed = await confirm({
      title: t('pages.settings:sections.deleteAccount.confirmTitle'),
      message: t('pages.settings:sections.deleteAccount.confirmMessage'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    const password = await prompt({
      title: t('pages.settings:sections.deleteAccount.confirmTitle'),
      message: t('pages.settings:sections.deleteAccount.passwordLabel'),
      defaultValue: '',
    });
    if (password == null || password.trim() === '') return;
    deleteAccountMutation.mutate({ password: password.trim() });
  };

  const handleDeleteAllDemoProjects = async () => {
    if (!activeOrganizationId) return;
    const confirmed = await confirm({
      title: t('pages.settings:sections.demoProjects.deleteConfirmTitle'),
      message: t('pages.settings:sections.demoProjects.deleteConfirmMessage'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    deleteAllDemoProjectsMutation.mutate();
  };

  // Position display value comes directly from currentUser (computed by API: Owner or position name)
  // No need to derive from formValues.position - the API returns the display-ready string
  const positionName = currentUser?.position || '';
  
  // Get role display name
  const getRoleDisplayName = (role: string | null): string => {
    if (!role) return t('pages.settings:sections.profile.roles.unknown') || 'Unknown';
    const roleKey = `pages.settings:sections.profile.roles.${role}`;
    return t(roleKey, { defaultValue: role });
  };
  
  // Use org-scoped resolved role only; never global users.role
  const roleDisplayName = getRoleDisplayName(
    (currentUser as any)?.resolvedOrgRole ?? (currentUser as any)?.orgRole ?? activeOrgRole ?? null
  );

  // Profile photo upload mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formDataUpload = new FormData();
      formDataUpload.append('photo', file);
      const res = await apiRequest('/api/uploads/profile-photo', {
        method: 'POST',
        body: formDataUpload,
      });
      return await res.json();
    },
    onSuccess: async (data) => {
      if (data.success && data.data?.url) {
        // Update profile with new photo URL
        const profileData = {
          ...formValues,
          profilePhotoUrl: data.data.url
        };
        setFormValues(profileData);
        setHasChanges(true);
        
        // Update profile via API (position is not sent - it's org-context-aware and computed by API)
        try {
          await apiRequest('/api/auth/update-profile', {
            method: 'POST',
            body: JSON.stringify({
              firstName: formValues.firstName,
              lastName: formValues.lastName,
              displayName: formValues.displayName,
              email: formValues.email,
              phone: formValues.phone,
              mobile: formValues.mobile,
              company: formValues.company,
              street: formValues.street,
              houseNumber: formValues.houseNumber,
              postalCode: formValues.postalCode,
              city: formValues.city,
              country: formValues.country,
              profilePhotoUrl: data.data.url
            }),
          });
        } catch (error) {
          console.error('Failed to update profile with photo URL:', error);
        }
        
        toast({
          title: t('pages.settings:sections.profile.photoUploadSuccess'),
          description: t('pages.settings:sections.profile.photoUploadSuccessDesc'),
        });
        
        // Invalidate user data query to refresh the profile photo
        queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
      }
    },
    onError: (error: any) => {
      toast({
        title: t('pages.settings:sections.profile.photoUploadError'),
        description: error?.message || t('pages.settings:sections.profile.photoUploadErrorDesc'),
        variant: 'destructive',
      });
    },
    onSettled: () => setIsUploadingPhoto(false),
  });

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: t('pages.settings:sections.profile.photoUploadError'),
        description: t('pages.settings:sections.profile.photoUploadInvalidType'),
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: t('pages.settings:sections.profile.photoUploadError'),
        description: t('pages.settings:sections.profile.photoUploadTooLarge'),
        variant: 'destructive',
      });
      return;
    }

    // Open crop modal instead of direct upload
    setSelectedImageFile(file);
    setShowCropModal(true);
    
    // Reset file input
    e.target.value = '';
  };

  const handleCropSave = (croppedFile: File) => {
    setShowCropModal(false);
    setSelectedImageFile(null);
    setIsUploadingPhoto(true);
    uploadPhotoMutation.mutate(croppedFile);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setSelectedImageFile(null);
  };

  const handleRemovePhoto = async () => {
    setFormValues({ ...formValues, profilePhotoUrl: '' });
    setHasChanges(true);
    
    // Update profile via API
    try {
      await apiRequest('/api/auth/update-profile', {
        method: 'POST',
        body: JSON.stringify({
          firstName: formValues.firstName,
          lastName: formValues.lastName,
          displayName: formValues.displayName,
          email: formValues.email,
          phone: formValues.phone,
          mobile: formValues.mobile,
          position: formValues.position,
          company: formValues.company,
          street: formValues.street,
          houseNumber: formValues.houseNumber,
          postalCode: formValues.postalCode,
          city: formValues.city,
          country: formValues.country,
          profilePhotoUrl: null
        }),
      });
    } catch (error) {
      console.error('Failed to remove profile photo:', error);
    }
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-6">
        {/* Linke Spalte - Alle Einstellungen */}
        <div className="space-y-1">
          {/* User Role Display - Read-only */}
          <Card className="settings-card-muted">
            <CardContent className="p-3">
              <div className="flex items-center gap-4">
                <label className="text-xs font-medium text-slate-700 whitespace-nowrap">
                  {t('pages.settings:sections.profile.userTypeLabel')}
                </label>
                <div className="flex-1 px-3 py-1.5 text-xs font-medium border border-slate-300 rounded bg-blue-500 text-white">
                  {roleDisplayName}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile Settings - EMIN Layout */}
          <Card className="settings-card-muted">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-slate-700">{t('pages.settings:sections.profile.title')}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-4">
              {/* Profile Photo Upload Section */}
              <div className="space-y-2 mb-3 pb-3 border-b border-slate-200">
                <div className="text-xs text-slate-600">{t('pages.settings:sections.profile.fields.profilePhoto')}</div>
                <div className="flex items-center gap-3">
                  {profilePhotoUrl ? (
                    <div className="relative">
                      <img
                        src={profilePhotoUrl}
                        alt="Profile photo"
                        className="h-20 w-20 object-cover border border-slate-200 rounded-full"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-red-500 hover:bg-red-600 text-white"
                        onClick={handleRemovePhoto}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ) : (
                    <div className="h-20 w-20 border-2 border-dashed border-slate-300 rounded-full flex items-center justify-center bg-slate-50">
                      <Upload className="h-6 w-6 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoUpload}
                      disabled={isUploadingPhoto}
                      className="hidden"
                      id="photo-upload"
                    />
                    <label htmlFor="photo-upload">
                      <Button
                        type="button"
                        variant="pillOutline"
                        size="sm"
                        className="bg-white hover:bg-white"
                        disabled={isUploadingPhoto}
                        asChild
                      >
                        <span>
                          {isUploadingPhoto ? (
                            <>
                              <LogoLoader size="sm" />
                              {t('pages.settings:sections.profile.photoUploading')}
                            </>
                          ) : (
                            <>
                              <Upload className="w-3 h-3 mr-2" />
                              {profilePhotoUrl ? t('pages.settings:sections.profile.photoChange') : t('pages.settings:sections.profile.photoUpload')}
                            </>
                          )}
                        </span>
                      </Button>
                    </label>
                    <p className="text-xs text-slate-500 mt-1">
                      {t('pages.settings:sections.profile.photoHint')}
                    </p>
                    </div>
      </div>
    </div>
              <div className="grid grid-cols-2 gap-4">
                <CornerLabelField label={t('pages.settings:sections.profile.fields.firstName')}>
                  <Input id="firstName" value={formValues.firstName} onChange={(e) => setFormValues((prev: any) => ({ ...prev, firstName: e.target.value }))} />
                </CornerLabelField>
                <CornerLabelField label={t('pages.settings:sections.profile.fields.lastName')}>
                  <Input id="lastName" value={formValues.lastName} onChange={(e) => setFormValues((prev: any) => ({ ...prev, lastName: e.target.value }))} />
                </CornerLabelField>
              </div>
              <CornerLabelField label={t('pages.settings:sections.profile.fields.email')}>
                <Input id="email" type="email" value={formValues.email} onChange={(e) => setFormValues((prev: any) => ({ ...prev, email: e.target.value }))} />
              </CornerLabelField>
              <div className="grid grid-cols-2 gap-4">
                <CornerLabelField label={t('pages.settings:sections.profile.fields.phone')}>
                  <PhoneInput
                    id="phone"
                    value={formValues.phone || ''}
                    onChange={(value) => setFormValues((prev: any) => ({ ...prev, phone: value }))}
                    defaultCountry={defaultCountry}
                    placeholder={t('pages.settings:sections.profile.fields.phonePlaceholder') as string}
                  />
                </CornerLabelField>
                <CornerLabelField label={t('pages.settings:sections.profile.fields.mobile')}>
                  <PhoneInput
                    id="mobile"
                    value={formValues.mobile || ''}
                    onChange={(value) => setFormValues((prev: any) => ({ ...prev, mobile: value }))}
                    defaultCountry={defaultCountry}
                    placeholder={t('pages.settings:sections.profile.fields.mobilePlaceholder') as string}
                  />
                </CornerLabelField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CornerLabelField label={t('pages.settings:sections.profile.fields.company')}>
                  <Input id="company" value={formValues.company} onChange={(e) => setFormValues((prev: any) => ({ ...prev, company: e.target.value }))} placeholder={t('pages.settings:sections.profile.fields.companyPlaceholder') as string} />
                </CornerLabelField>
                <CornerLabelField label={t('pages.settings:sections.profile.fields.position')}>
                  <Input id="position" value={positionName} readOnly className="bg-gray-50 text-[#666666] cursor-not-allowed" disabled />
                </CornerLabelField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CornerLabelField label={t('pages.settings:sections.profile.fields.street')}>
                  <Input id="street" value={formValues.street} onChange={(e) => setFormValues((prev: any) => ({ ...prev, street: e.target.value }))} placeholder={t('pages.settings:sections.profile.fields.streetPlaceholder') as string} />
                </CornerLabelField>
                <CornerLabelField label={t('pages.settings:sections.profile.fields.houseNumber')}>
                  <Input id="houseNumber" value={formValues.houseNumber} onChange={(e) => setFormValues((prev: any) => ({ ...prev, houseNumber: e.target.value }))} placeholder={t('pages.settings:sections.profile.fields.houseNumberPlaceholder') as string} />
                </CornerLabelField>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <CornerLabelField label={t('pages.settings:sections.profile.fields.postalCode')}>
                  <Input id="postalCode" value={formValues.postalCode} onChange={(e) => setFormValues((prev: any) => ({ ...prev, postalCode: e.target.value }))} placeholder={t('pages.settings:sections.profile.fields.postalCodePlaceholder') as string} />
                </CornerLabelField>
                <CornerLabelField label={t('pages.settings:sections.profile.fields.city')}>
                  <Input id="city" value={formValues.city} onChange={(e) => setFormValues((prev: any) => ({ ...prev, city: e.target.value }))} placeholder={t('pages.settings:sections.profile.fields.cityPlaceholder') as string} />
                </CornerLabelField>
              </div>
              <CornerLabelField label={t('pages.settings:sections.profile.fields.country')}>
                <Select
                  value={formValues.country || ''}
                  onValueChange={(value) => setFormValues((prev: any) => ({ ...prev, country: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('pages.settings:sections.profile.fields.countryPlaceholder') as string} />
                  </SelectTrigger>
                  <SelectContent>
                    {getSortedCountries(i18n.language as 'en' | 'de' | 'tr').map((country) => (
                      <SelectItem key={country.code} value={country.code} className="text-xs">
                        {country[i18n.language as 'en' | 'de' | 'tr']}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CornerLabelField>
              <CornerLabelField label={t('pages.settings:sections.profile.fields.userId')}>
                <Input id="userCode" value={currentUser?.userCode || 'Lade...'} readOnly className="bg-gray-50 text-[#666666] cursor-not-allowed" disabled />
              </CornerLabelField>
            </CardContent>
          </Card>

          {/* Security Settings */}
          <Card className="settings-card-muted">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-slate-700">{t('pages.settings:sections.password.title')}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-1">
              <div className="text-xs text-slate-500 mb-2">{t('pages.settings:sections.password.description')}</div>
              <Button onClick={() => setShowPasswordDialog(true)} variant="pillOutline" size="sm" className="bg-white hover:bg-white">
                {t('pages.settings:sections.password.changeButton')}
              </Button>
            </CardContent>
          </Card>

        </div>

        {/* Rechte Spalte */}
        <div className="space-y-1">
          {/* Language Settings */}
          <Card className="settings-card-muted">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-slate-700">{t('pages.settings:sections.language.title') || 'Language'}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-4">
              <CornerLabelField label={t('pages.settings:sections.language.label') || 'Select Language'}>
                <LanguageSwitcher compact={true} variant="default" />
              </CornerLabelField>
            </CardContent>
          </Card>

          {/* Timezone Settings */}
          <Card className="settings-card-muted">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-slate-700">{t('pages.settings:sections.time.title')}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-6">
              <CornerLabelField label={t('pages.settings:sections.time.timezone')}>
                <Select value={formValues.timezone} onValueChange={(value) => { setFormValues((prev: any) => ({ ...prev, timezone: value })); setHasChanges(true); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Europe */}
                    <SelectItem value="Europe/London">London (UTC+0/+1)</SelectItem>
                    <SelectItem value="Europe/Dublin">Dublin (UTC+0/+1)</SelectItem>
                    <SelectItem value="Europe/Lisbon">Lissabon (UTC+0/+1)</SelectItem>
                    <SelectItem value="Europe/Berlin">Berlin (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Vienna">Wien (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Zurich">Zürich (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Paris">Paris (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Amsterdam">Amsterdam (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Brussels">Brüssel (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Rome">Rom (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Madrid">Madrid (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Warsaw">Warschau (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Prague">Prag (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Stockholm">Stockholm (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Oslo">Oslo (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Copenhagen">Kopenhagen (UTC+1/+2)</SelectItem>
                    <SelectItem value="Europe/Helsinki">Helsinki (UTC+2/+3)</SelectItem>
                    <SelectItem value="Europe/Athens">Athen (UTC+2/+3)</SelectItem>
                    <SelectItem value="Europe/Bucharest">Bukarest (UTC+2/+3)</SelectItem>
                    <SelectItem value="Europe/Kiev">Kiew (UTC+2/+3)</SelectItem>
                    <SelectItem value="Europe/Istanbul">Istanbul (UTC+3)</SelectItem>
                    <SelectItem value="Europe/Moscow">Moskau (UTC+3)</SelectItem>
                    {/* Middle East & Africa */}
                    <SelectItem value="Asia/Dubai">Dubai (UTC+4)</SelectItem>
                    <SelectItem value="Asia/Riyadh">Riad (UTC+3)</SelectItem>
                    <SelectItem value="Asia/Tehran">Teheran (UTC+3:30)</SelectItem>
                    <SelectItem value="Africa/Cairo">Kairo (UTC+2)</SelectItem>
                    <SelectItem value="Africa/Johannesburg">Johannesburg (UTC+2)</SelectItem>
                    <SelectItem value="Africa/Lagos">Lagos (UTC+1)</SelectItem>
                    <SelectItem value="Africa/Nairobi">Nairobi (UTC+3)</SelectItem>
                    {/* Asia & Pacific */}
                    <SelectItem value="Asia/Kolkata">Mumbai (UTC+5:30)</SelectItem>
                    <SelectItem value="Asia/Karachi">Karachi (UTC+5)</SelectItem>
                    <SelectItem value="Asia/Dhaka">Dhaka (UTC+6)</SelectItem>
                    <SelectItem value="Asia/Bangkok">Bangkok (UTC+7)</SelectItem>
                    <SelectItem value="Asia/Jakarta">Jakarta (UTC+7)</SelectItem>
                    <SelectItem value="Asia/Singapore">Singapur (UTC+8)</SelectItem>
                    <SelectItem value="Asia/Hong_Kong">Hongkong (UTC+8)</SelectItem>
                    <SelectItem value="Asia/Shanghai">Shanghai (UTC+8)</SelectItem>
                    <SelectItem value="Asia/Taipei">Taipeh (UTC+8)</SelectItem>
                    <SelectItem value="Asia/Seoul">Seoul (UTC+9)</SelectItem>
                    <SelectItem value="Asia/Tokyo">Tokio (UTC+9)</SelectItem>
                    {/* Oceania */}
                    <SelectItem value="Australia/Perth">Perth (UTC+8)</SelectItem>
                    <SelectItem value="Australia/Sydney">Sydney (UTC+10/+11)</SelectItem>
                    <SelectItem value="Australia/Melbourne">Melbourne (UTC+10/+11)</SelectItem>
                    <SelectItem value="Pacific/Auckland">Auckland (UTC+12/+13)</SelectItem>
                    {/* Americas */}
                    <SelectItem value="America/Sao_Paulo">São Paulo (UTC-3)</SelectItem>
                    <SelectItem value="America/Argentina/Buenos_Aires">Buenos Aires (UTC-3)</SelectItem>
                    <SelectItem value="America/New_York">New York (UTC-5/-4)</SelectItem>
                    <SelectItem value="America/Toronto">Toronto (UTC-5/-4)</SelectItem>
                    <SelectItem value="America/Chicago">Chicago (UTC-6/-5)</SelectItem>
                    <SelectItem value="America/Mexico_City">Mexiko-Stadt (UTC-6/-5)</SelectItem>
                    <SelectItem value="America/Denver">Denver (UTC-7/-6)</SelectItem>
                    <SelectItem value="America/Los_Angeles">Los Angeles (UTC-8/-7)</SelectItem>
                    <SelectItem value="America/Vancouver">Vancouver (UTC-8/-7)</SelectItem>
                    <SelectItem value="America/Anchorage">Anchorage (UTC-9/-8)</SelectItem>
                    <SelectItem value="Pacific/Honolulu">Honolulu (UTC-10)</SelectItem>
                  </SelectContent>
                </Select>
              </CornerLabelField>
              <CornerLabelField label={t('pages.settings:sections.time.dateFormat')}>
                <Select 
                  value={preferences?.dateFormat || 'dd.MM.yyyy'} 
                  onValueChange={(value) => updatePreferences.mutate({ dateFormat: value })} 
                  disabled={preferencesLoading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dd.MM.yyyy">{t('pages.settings:sections.time.dateFormats.ddMMyyyy')}</SelectItem>
                    <SelectItem value="MM/dd/yyyy">{t('pages.settings:sections.time.dateFormats.MMddyyyy')}</SelectItem>
                    <SelectItem value="yyyy-MM-dd">{t('pages.settings:sections.time.dateFormats.yyyyMMdd')}</SelectItem>
                    <SelectItem value="dd/MM/yyyy">{t('pages.settings:sections.time.dateFormats.ddMMyyyyAlt')}</SelectItem>
                    <SelectItem value="MM.dd.yyyy">{t('pages.settings:sections.time.dateFormats.MMddyyyyAlt')}</SelectItem>
                  </SelectContent>
                </Select>
              </CornerLabelField>
            </CardContent>
          </Card>


          {/* WhatsApp-Benachrichtigungen (uses profile mobile number) */}
          <Card className="settings-card-muted">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-slate-700">{t('pages.settings:sections.whatsapp.title')}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600">{t('pages.settings:sections.whatsapp.enabled')}</span>
                <Switch checked={preferences?.whatsappNotifications || false} onCheckedChange={(checked) => updatePreferences.mutate({ whatsappNotifications: checked })} disabled={preferencesLoading} />
              </div>
              <div className="text-xs text-slate-500">{t('pages.settings:sections.whatsapp.descriptionEnabled')}</div>
              <div className="text-xs text-slate-500">{t('pages.settings:sections.whatsapp.usesProfileMobile')}</div>
              {false && (
              <div className="pt-1">
                <Button 
                  variant="pillOutline" 
                  className="bg-white hover:bg-white w-full" 
                  disabled={preferencesLoading || testWhatsAppMutation.isPending || !formValues?.mobile?.trim()}
                  onClick={() => testWhatsAppMutation.mutate()}
                >
                  {testWhatsAppMutation.isPending ? <LogoLoader size="sm" /> : t('pages.settings:sections.whatsapp.testButton')}
                </Button>
              </div>
              )}
            </CardContent>
          </Card>

          {/* Data protection – functional cookies */}
          <Card className="settings-card-muted">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-slate-700">{t('pages.settings:sections.dataProtection.title')}</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-600">{t('pages.settings:sections.dataProtection.cookies')}</span>
                <Switch checked={preferences?.cookies ?? false} onCheckedChange={(checked) => updatePreferences.mutate({ cookies: checked })} disabled={preferencesLoading} />
              </div>
              <div className="text-xs text-slate-500">{t('pages.settings:sections.dataProtection.cookiesDescription')}</div>
            </CardContent>
          </Card>

          <Card className="settings-card-muted">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-slate-700">
                {t('pages.settings:sections.includedProjects.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              {isIncludedProjectsLoading ? (
                <div className="flex justify-center py-4">
                  <LogoLoader size="sm" />
                </div>
              ) : includedProjects.length === 0 ? (
                <div className="text-xs text-slate-500">
                  {t('pages.settings:sections.includedProjects.empty')}
                </div>
              ) : (
                includedProjects.map((project: any) => (
                  <div key={project.id} className="flex items-center justify-between gap-2 py-1">
                    <div className="min-w-0">
                      <p className="text-xs text-slate-700 truncate">{project.name}</p>
                      {project.projektNummer && (
                        <p className="text-[10px] text-slate-500 truncate">{project.projektNummer}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {canDeleteProjectsFromAccount && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={
                            leaveProjectMutation.isPending ||
                            deleteProjectFromAccountMutation.isPending
                          }
                          onClick={async () => {
                            const confirmed = await confirm({
                              title: t('pages.settings:sections.includedProjects.deleteConfirmTitle'),
                              message: t('pages.settings:sections.includedProjects.deleteConfirmMessage', {
                                projectName: project.name,
                              }),
                              variant: 'destructive',
                            });
                            if (!confirmed) return;
                            deleteProjectFromAccountMutation.mutate(project.id);
                          }}
                        >
                          {t('pages.settings:sections.includedProjects.deleteAction')}
                        </Button>
                      )}
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={
                          leaveProjectMutation.isPending ||
                          deleteProjectFromAccountMutation.isPending
                        }
                        onClick={async () => {
                          const confirmed = await confirm({
                            title: t('pages.settings:sections.includedProjects.leaveConfirmTitle'),
                            message: t('pages.settings:sections.includedProjects.leaveConfirmMessage', {
                              projectName: project.name,
                            }),
                            variant: 'destructive',
                          });
                          if (!confirmed) return;
                          leaveProjectMutation.mutate(project.id);
                        }}
                      >
                        {t('pages.settings:sections.includedProjects.leaveAction')}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="settings-card-muted">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-slate-700">
                {t('pages.settings:sections.includedOrganizations.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              {isIncludedOrgsLoading ? (
                <div className="flex justify-center py-4">
                  <LogoLoader size="sm" />
                </div>
              ) : includedOrganizations.length === 0 ? (
                <div className="text-xs text-slate-500">
                  {t('pages.settings:sections.includedOrganizations.empty')}
                </div>
              ) : (
                includedOrganizations.map((org: any) => (
                  <div key={org.id} className="flex items-center justify-between gap-2 py-1">
                    <div className="min-w-0">
                      <p className="text-xs text-slate-700 truncate">{org.name}</p>
                      {org.relation && (
                        <p className="text-[10px] text-slate-500 truncate capitalize">{org.relation}</p>
                      )}
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={leaveOrganizationMutation.isPending}
                      onClick={async () => {
                        const confirmed = await confirm({
                          title: t('pages.settings:sections.includedOrganizations.leaveConfirmTitle'),
                          message: t('pages.settings:sections.includedOrganizations.leaveConfirmMessage', {
                            orgName: org.name,
                          }),
                          variant: 'destructive',
                        });
                        if (!confirmed) return;
                        leaveOrganizationMutation.mutate(org.id);
                      }}
                    >
                      {t('pages.settings:sections.includedOrganizations.leaveAction')}
                    </Button>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {hasPermission('manage_organization') && activeOrganizationId && (
            <Card className="settings-card-muted bg-transparent">
              <CardHeader className="py-2 px-3">
                <CardTitle className="text-xs font-medium text-slate-700">
                  {t('pages.settings:sections.demoProjects.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-[#333333]">
                      {t('pages.settings:sections.demoProjects.hideLabel')}
                    </p>
                    <p className="text-xs text-[#666666]">
                      {t('pages.settings:sections.demoProjects.hideDescription')}
                    </p>
                  </div>
                  <Switch
                    checked={hideDemoProjects}
                    disabled={isDemoSettingsLoading || updateDemoSettingsMutation.isPending}
                    onCheckedChange={(checked) => updateDemoSettingsMutation.mutate(checked)}
                  />
                </div>
                <Separator />
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#333333]">
                    {t('pages.settings:sections.demoProjects.deleteLabel')}
                  </p>
                  <p className="text-xs text-[#666666]">
                    {t('pages.settings:sections.demoProjects.deleteDescription')}
                  </p>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      void handleDeleteAllDemoProjects();
                    }}
                    disabled={deleteAllDemoProjectsMutation.isPending}
                  >
                    {t('pages.settings:sections.demoProjects.deleteAction')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="settings-card-muted border-red-200">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-xs font-medium text-red-700">
                {t('pages.settings:sections.deleteAccount.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              <div className="text-xs text-slate-600">
                {t('pages.settings:sections.deleteAccount.description')}
              </div>
              <Button
                variant="destructive"
                size="sm"
                disabled={deleteAccountMutation.isPending}
                onClick={handleDeleteAccount}
                className="w-full"
              >
                {deleteAccountMutation.isPending ? (
                  <>
                    <LogoLoader size="sm" className="inline-block mr-2" />
                    {t('pages.settings:sections.deleteAccount.deleting')}
                  </>
                ) : (
                  t('pages.settings:sections.deleteAccount.button')
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Profile Photo Crop Modal */}
      <ProfilePhotoCropModal
        isOpen={showCropModal}
        imageFile={selectedImageFile}
        onClose={handleCropCancel}
        onSave={handleCropSave}
      />
    </div>
  );
}
